import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import BrandLogo from "@/components/brand/BrandLogo";
import { Button, Checkbox, Field, Input, PasswordInput } from "@/components/ui-kit";

const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [password2, setPassword2] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Erro de campo aparece no campo, enquanto a pessoa digita. Antes a senha
  // divergente só ficava com a borda vermelha e o motivo aparecia lá no topo,
  // depois de enviar.
  const mismatch = password2.length > 0 && form.password !== password2;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== password2) { setError("As senhas não conferem."); return; }
    if (!consent) { setError("Aceite o tratamento dos seus dados (LGPD) para continuar."); return; }
    setError(""); setLoading(true);
    try { await register(form); navigate("/"); }
    catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    /* No celular, `p-8` por fora MAIS `p-8` no cartão deixavam 262px úteis numa
       tela de 390px. O cartão só ganha moldura a partir de `sm`; abaixo disso
       ele encosta na borda, como no login. */
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 sm:p-8 bg-slate-50">
      <Link to="/" className="inline-flex mb-6" data-testid="register-logo-link">
        <BrandLogo size={36} typeHeight={22} />
      </Link>
      <form
        onSubmit={submit}
        className="w-full max-w-sm sm:bg-white sm:border sm:border-slate-200 sm:rounded-xl sm:p-8"
        data-testid="register-form"
      >
        <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-6">Criar conta</h1>
        {error && (
          <div role="alert" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="register-error">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <Field label="Nome completo" labelTone="plain" required>
            <Input value={form.name} onChange={set("name")} autoComplete="name" size="lg" data-testid="register-name-input" />
          </Field>
          <Field label="E-mail" labelTone="plain" required>
            {/* Mesmo motivo do login: o teclado do celular capitaliza e corrige
                o e-mail por conta própria, e o erro só aparece ao enviar. */}
            <Input type="email" value={form.email} onChange={set("email")} autoComplete="email"
              inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
              size="lg" data-testid="register-email-input" />
          </Field>
          <Field label="Telefone" labelTone="plain" hint="Usamos para avisar sobre a entrega.">
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })}
              inputMode="tel" autoComplete="tel" placeholder="(11) 99999-9999" size="lg"
              data-testid="register-phone-input"
            />
          </Field>
          <Field label="Senha" labelTone="plain" required hint="Mínimo de 6 caracteres.">
            <PasswordInput value={form.password} onChange={set("password")} minLength={6}
              autoComplete="new-password" size="lg" data-testid="register-password-input" />
          </Field>
          <Field
            label="Confirmar senha"
            labelTone="plain"
            required
            error={mismatch ? "As senhas não conferem." : undefined}
          >
            <PasswordInput value={password2} onChange={(e) => setPassword2(e.target.value)} minLength={6}
              autoComplete="new-password" size="lg" data-testid="register-password2-input" />
          </Field>
        </div>

        <Checkbox
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
          data-testid="register-consent"
          className="items-start mt-5 mb-5 text-xs text-slate-600"
          label={<span>Li e autorizo o tratamento dos meus dados pessoais para atendimento e compras, conforme a <b>LGPD</b>.</span>}
        />

        <Button type="submit" size="xl" block loading={loading} data-testid="register-submit-btn">
          {loading ? "Criando..." : "Cadastrar"}
        </Button>
        <p className="text-sm text-slate-500 mt-4 text-center">
          Já tem conta? <Link to="/login" data-testid="register-login-link" className="text-brand-600 font-medium hover:underline">Entrar</Link>
        </p>
      </form>
    </div>
  );
}
